import DOMPurify from "dompurify";

const PURIFY_HTML: DOMPurify.Config = { USE_PROFILES: { html: true } };

/**
 * Strip `<script>…</script>` and trailing unclosed `<script>…` before DOMPurify. Some parsers return
 * an empty fragment when script markup sits next to other tags without this pass.
 */
function stripScriptTagsBeforePurify(source: string): string {
  let s = source;
  for (let i = 0; i < 32; i++) {
    const next = s.replace(/<script\b[\s\S]*?<\/script>/gi, "");
    if (next === s) break;
    s = next;
  }
  return s.replace(/<script\b[\s\S]*$/gi, "");
}

function looksLikeHtmlDocument(source: string): boolean {
  const head = source.trim().slice(0, 1200).toLowerCase();
  return (
    head.includes("<!doctype") ||
    /<\s*html[\s>]/.test(head) ||
    (/<\s*head[\s>]/.test(head) && /<\s*body[\s>]/.test(head))
  );
}

/**
 * Sanitized HTML suitable for embedding (fragment or full document string).
 * Full documents keep `<head>` / `<body>` structure (styles, meta, etc.) after sanitization.
 */
export function htmlSourceToSafePreviewHtml(source: string): string {
  const trimmed = stripScriptTagsBeforePurify(source).trim();
  if (!trimmed) return "";
  if (looksLikeHtmlDocument(trimmed)) {
    return DOMPurify.sanitize(trimmed, { ...PURIFY_HTML, WHOLE_DOCUMENT: true });
  }
  return DOMPurify.sanitize(trimmed, PURIFY_HTML);
}

/**
 * Complete HTML document for `iframe.srcdoc` or `document.write` in a popup.
 * Full buffers: sanitized as a whole document. Fragments: wrapped in a minimal shell.
 */
export function buildBrowserLikePreviewSrcdoc(source: string): string {
  const trimmed = stripScriptTagsBeforePurify(source).trim();
  if (!trimmed) {
    return "<!DOCTYPE html><html lang=\"en\"><head><meta charset=\"utf-8\"></head><body></body></html>";
  }
  if (looksLikeHtmlDocument(trimmed)) {
    let doc = DOMPurify.sanitize(trimmed, { ...PURIFY_HTML, WHOLE_DOCUMENT: true });
    const lower = doc.trimStart().slice(0, 20).toLowerCase();
    if (!lower.startsWith("<!doctype")) {
      doc = `<!DOCTYPE html>\n${doc}`;
    }
    return doc;
  }
  const bodyHtml = DOMPurify.sanitize(trimmed, PURIFY_HTML);
  return `<!DOCTYPE html><html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><title>Preview</title></head><body>${bodyHtml}</body></html>`;
}

/** Element to rasterize for PDF export when the preview is an iframe. */
export function htmlPreviewPdfCaptureRoot(frame: HTMLIFrameElement): HTMLElement {
  const doc = frame.contentDocument;
  const root = doc?.documentElement ?? doc?.body;
  return (root ?? frame) as HTMLElement;
}
